/**
 * 무용연 권한 매트릭스 — 단일 소스.
 *
 * 기획 8절 회원유형별 권한표를 코드화한다. 화면은 직접 memberType 을 검사하지 말고
 * 반드시 can(user, action) / usePermission().can(action) 을 거친다.
 *
 * ⚠️ 프론트 게이팅은 UX일 뿐 보안이 아니다. 백엔드(muyeon-backend)도 동일 액션키로
 *    서버 측 가드를 두어야 한다. 액션키/허용유형을 양쪽 동기화할 것.
 *
 * 확정 정책(2026-06-24):
 *  - 지원(job/sub): 강사 + 무용수            [1-A]
 *  - 강사 이력서 열람: 학원/원장 + 공연팀     [2-A]
 *  - 멀티 유형 보유 → 권한은 합집합           [3-A]
 */

// 특수 규칙 토큰
export const ALL = "ALL"; // 비회원 포함 누구나
export const LOGIN = "LOGIN"; // 로그인했으면 유형 무관

// 액션 → 허용 (ALL | LOGIN | [유형코드...])
export const ACTIONS = {
  // 강사채용
  "job.view": ALL,
  "job.apply": ["TEACHER", "DANCER"],
  "job.create": ["ACADEMY", "TEAM"],

  // 대타공고
  "sub.view": ALL,
  "sub.apply": ["TEACHER", "DANCER"],
  "sub.create": ["TEACHER", "ACADEMY", "DANCER", "TEAM"],

  // 레슨(클래스) 상품 — 강사·학원만 개설(2026-09-26: 무용수는 레슨 활동 전면 불가, 강사 유형을 따야 함)
  "lesson.view": ALL,
  "lesson.create": ["TEACHER", "ACADEMY"],

  // 공연공고(캐스팅)
  "casting.view": ALL,
  "casting.apply": ["DANCER"],
  "casting.create": ["TEAM"],

  // 공간대관
  "space.view": ALL,
  "space.inquiry": LOGIN,
  "space.create": ["SPACE"],

  // 커뮤니티
  "community.view": ALL,
  "community.write": LOGIN,

  // 열람
  "resume.read": ["ACADEMY", "TEAM"], // 강사프로필 상세 열람(학원·원장·공연팀)
  "resume.write": ["TEACHER", "DANCER"], // 구직 등록(내 이력서/프로필) — 강사·무용수
  "dancer.read": ["TEAM", "ACADEMY", "TEACHER"], // 무용수 상세 — 공연팀은 캐스팅, 학원·강사는 채팅
  "dancer.video": ["DANCER", "TEAM"], // 무용수 영상 (본인 여부는 호출부에서 별도 처리)
};

// 거부 시 안내 문구 — "MY > 회원 유형 변경/인증에서 회원유형을 추가하고 인증을 완료해 주세요" 로 유도.
const GUIDE = "MY > 회원유형 설정에서 유형을 추가해 주세요.";
// 학원·공연팀은 서류 인증까지 마쳐야 한다(강사·무용수는 휴대폰 본인인증만 필요).
const GUIDE_VERIFY = "MY > 회원유형 설정에서 유형을 추가하고 인증을 마쳐 주세요.";
export const GATE_MESSAGES = {
  "job.apply": `구직은 강사·무용수 회원만 이용할 수 있어요. ${GUIDE}`,
  "job.create": `채용공고는 학원·공연팀 회원만 등록할 수 있어요. ${GUIDE_VERIFY}`,
  "sub.apply": `대타공고는 강사·무용수 회원만 지원할 수 있어요. ${GUIDE}`,
  "sub.create": `대타공고는 학원·강사·무용수·공연팀 회원만 등록할 수 있어요. ${GUIDE}`,
  "casting.apply": `공연공고는 무용수 회원만 지원할 수 있어요. ${GUIDE}`,
  "casting.create": `공연공고는 공연팀·기획자 회원만 등록할 수 있어요. ${GUIDE_VERIFY}`,
  "lesson.create": `레슨은 강사·학원 회원만 개설할 수 있어요. ${GUIDE}`,
  "space.create": `공간은 공간 보유자 회원만 등록할 수 있어요. ${GUIDE}`,
  "resume.read": `강사 상세 프로필은 학원·공연팀 회원만 볼 수 있어요. ${GUIDE_VERIFY}`,
  "resume.write": `구직 등록은 강사·무용수 회원만 할 수 있어요. ${GUIDE}`,
  "dancer.read": `무용수 상세 프로필은 공연팀·학원·강사 회원만 볼 수 있어요. ${GUIDE}`,
  "dancer.video": `무용수 영상은 공연팀·기획자 회원만 볼 수 있어요. ${GUIDE}`,
  default: `지금 회원유형으로는 이용할 수 없는 기능이에요. ${GUIDE}`,
};

// 보유 유형 코드 배열 반환 (권한 = 보유 유형 합집합). 레거시 단일 memberType 은 제거됨.
export const heldTypes = (user) => {
  if (!user) return [];
  return Array.isArray(user.memberTypes) ? user.memberTypes : [];
};

/** 액션 허용 여부. user=null 은 비회원. 권한은 보유 유형 합집합으로 판정. */
export const can = (user, action) => {
  const rule = ACTIONS[action];
  if (rule === undefined) return false;
  if (rule === ALL) return true;
  if (rule === LOGIN) return !!user;
  return heldTypes(user).some((t) => rule.includes(t));
};

export const gateMessage = (action) => GATE_MESSAGES[action] || GATE_MESSAGES.default;

/** 이 액션이 '로그인만' 요구하는지(유형 제한 없음) — 게이팅 분기에 사용 */
export const isLoginOnly = (action) => ACTIONS[action] === LOGIN;

/* ── 회원유형 파생 판정(중앙화) ──
 * 곳곳에서 `held.includes("TEACHER")` / `["TEACHER","ACADEMY"].some(...)` 로 재정의되던
 * isPro 등을 한 곳에서 정의. isPro 가 두 의미로 혼용되던 문제를 이름으로 구분한다. */
export const hasAnyType = (user, ...types) => {
  const held = heldTypes(user);
  return types.some((t) => held.includes(t));
};
/** 구인구직/캐스팅 등 pro 모드 유형(하단 탭바 pro 탭 노출 기준) */
export const isPro = (user) => hasAnyType(user, "TEACHER", "DANCER", "ACADEMY", "TEAM");
/** 사업역할(인증 필요) 보유 여부 */
export const isBiz = (user) => hasAnyType(user, "ACADEMY", "SPACE", "TEAM");
/** 견적을 '받아 응답'하는 쪽 — 강사·학원 (받은견적 팝업/견적 허브 보낸탭) */
export const canReceiveQuote = (user) => hasAnyType(user, "TEACHER", "ACADEMY");
/** 캐스팅 참여 쪽 — 무용수(지원)·공연팀(등록) */
export const isCastingSide = (user) => hasAnyType(user, "DANCER", "TEAM");
/** 일반회원(강사·무용수·학원·공연팀·공간 유형 전무) — 홈 개편뷰 대상 */
export const isGeneral = (user) => !hasAnyType(user, "TEACHER", "DANCER", "ACADEMY", "TEAM", "SPACE");

/* ── 활성유형(단일 활동 컨텍스트) 기준 판정 ──
 * Model B: 화면(탭/홈/MY)은 "지금 활동 중인 1개 유형" 기준으로만 노출.
 * (백엔드 권한·알림은 보유유형 합집합 유지 — 아래는 순수 UI 렌즈) */
const PRO_SET = ["TEACHER", "DANCER", "ACADEMY", "TEAM"];
/** 활성유형으로 이 액션이 가능한지 (합집합 can 의 활성유형판) */
export const canAs = (activeType, action) => {
  const rule = ACTIONS[action];
  if (rule === undefined) return false;
  if (rule === ALL) return true;
  if (rule === LOGIN) return true; // 로그인 사용자면 유형 무관(고객 액션)
  return activeType ? rule.includes(activeType) : false;
};
export const isProAs = (activeType) => PRO_SET.includes(activeType);
export const canReceiveQuoteAs = (activeType) => activeType === "TEACHER" || activeType === "ACADEMY";
/** 레슨을 '요청·예약'하는 쪽(고객)으로 쓸 수 있는지 — 무용수 활동 중에는 레슨 활동 전면 불가(2026-09-26).
 *  서버도 X-Active-Type=DANCER 면 POST /quotes·/lesson-reservations 를 SWITCH_REQUIRED(→GENERAL)로 막는다. */
export const canRequestLessonAs = (activeType) => activeType !== "DANCER";
export const isCastingSideAs = (activeType) => activeType === "DANCER" || activeType === "TEAM";
/** 활성유형이 일반(고객) 모드인지 — GENERAL 또는 pro/공간 아님 */
export const isGeneralAs = (activeType) => ![...PRO_SET, "SPACE"].includes(activeType);

/** 활성유형 → 네이티브 채팅 리스트 기본 세그먼트(all|requested|responded|inquiry) */
export const chatFilterForActive = (activeType) => {
  if (activeType === "TEACHER" || activeType === "ACADEMY") return "responded"; // 견적 응답(강사)
  if (activeType === "SPACE") return "inquiry"; // 공간 문의
  if (isGeneralAs(activeType)) return "requested"; // 일반=내 견적 요청
  return "all"; // 무용수·공연팀 등
};
